import Typography from "antd/lib/typography";
import { TFunction } from "i18next";

import { Feature } from "@sol/features";
import { interpolate, Route } from "@sol/routing";
import { getResourceUuid, IClassroom } from "@sol/sdk";
import { Binder } from "@sol/uikit/core/icons/Binder";
import { Brief } from "@sol/uikit/core/icons/Brief";
import { Clipboard } from "@sol/uikit/core/icons/Clipboard";
import { EmptyWork } from "@sol/uikit/core/icons/EmptyWork";
import { Globe01 } from "@sol/uikit/core/icons/General/Globe01";
import { Home04 } from "@sol/uikit/core/icons/General/Home04";
import { ShieldCheck } from "@sol/uikit/core/icons/Security/ShieldCheck";
import { Statistics } from "@sol/uikit/core/icons/Statistics";
import { Users } from "@sol/uikit/core/icons/Users";

import { getLink, MenuItem } from "./getLink";

/** The tree is the only referential screen, so it stays active on every record it opens. */
const FRAMEWORK_ADMIN_ROUTES: Route[] = [
    Route.ADMIN_FRAMEWORKS,
    Route.ADMIN_SKILLS,
    Route.ADMIN_SKILL_DOMAINS,
    Route.ADMIN_SKILL_BLOCKS,
];

export const MENU_OWNED_ROUTES: Record<string, string[]> = {
    [Route.ADMIN_FRAMEWORKS]: FRAMEWORK_ADMIN_ROUTES,
};

const menuLabel = (label: string, dataCy?: string) => (
    <Typography.Text className="text-ant-sm text-ellipsis text-inherit hover:cursor-pointer" data-cy={dataCy}>
        {label}
    </Typography.Text>
);

export const getMenuItems = ({
    t,
    checkFeatureFlag,
    activeClassroom,
}: {
    t: TFunction;
    checkFeatureFlag: (feature: Feature | Feature[]) => boolean;
    activeClassroom?: IClassroom;
}): MenuItem[] => {
    const items: MenuItem[] = [];

    // Home
    if (checkFeatureFlag(Feature.MENU_HOME)) {
        items.push(
            getLink({
                "data-cy": "menu-item-home",
                href: Route.HOME,
                label: menuLabel(t("component.Navigation.Menu.items.home")),
                icon: <Home04 stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    // Educational design
    if (checkFeatureFlag(Feature.TRAINING_TEMPLATES_MANAGE)) {
        items.push(
            getLink({
                label: t("component.Navigation.Menu.items.educationalDesign.trainingTemplatesManagement"),
                href: Route.TRAINING_TEMPLATES,
                icon: <Brief stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    // Brief administration
    if (checkFeatureFlag(Feature.BRIEFS_ADMIN)) {
        items.push(
            getLink({
                "data-cy": "menu-item-admin-briefs",
                href: Route.ADMIN_BRIEFS,
                label: menuLabel(t("component.Navigation.Menu.items.briefs")),
                icon: <Brief stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    if (checkFeatureFlag(Feature.ADMIN_PAGES_MANAGE)) {
        items.push(
            getLink({
                "data-cy": "menu-item-admin-pages",
                href: Route.ADMIN_PAGES,
                label: menuLabel(t("admin.pages.list.title")),
                icon: <Binder stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    if (checkFeatureFlag(Feature.ADMIN_ANNOUNCEMENTS_MANAGE)) {
        items.push(
            getLink({
                "data-cy": "menu-item-admin-announcements",
                href: Route.ADMIN_ANNOUNCEMENTS,
                label: menuLabel(t("admin.announcements.list.title")),
                icon: <Binder stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    // Framework administration
    if (checkFeatureFlag(Feature.FRAMEWORKS_ADMIN_READ)) {
        items.push(
            getLink({
                "data-cy": "menu-item-admin-frameworks",
                href: Route.ADMIN_FRAMEWORKS,
                label: menuLabel(t("component.Navigation.Menu.items.frameworks")),
                icon: <Binder stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    if (checkFeatureFlag(Feature.LANGUAGES_VIEW)) {
        items.push(
            getLink({
                "data-cy": "menu-item-admin-languages",
                href: Route.LANGUAGES,
                label: menuLabel(t("admin.languages.list.title")),
                icon: <Globe01 stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    if (checkFeatureFlag(Feature.TAGS_VIEW)) {
        items.push(
            getLink({
                "data-cy": "menu-item-admin-tags",
                href: Route.TAGS,
                label: menuLabel(t("admin.tags.list.title")),
                icon: <Binder stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    if (checkFeatureFlag(Feature.CATEGORIES_VIEW)) {
        items.push(
            getLink({
                "data-cy": "menu-item-admin-categories",
                href: Route.CATEGORIES,
                label: menuLabel(t("admin.categories.list.title")),
                icon: <Binder stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    if (checkFeatureFlag(Feature.ADMIN_STATISTICS)) {
        items.push(
            getLink({
                "data-cy": "menu-item-admin-statistics",
                href: Route.STATISTICS,
                label: menuLabel(t("admin.statistics.list.title")),
                icon: <Statistics stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    if (checkFeatureFlag(Feature.CERTIFICATION_ISSUERS_MANAGE)) {
        items.push(
            getLink({
                "data-cy": "menu-item-admin-certification-issuers",
                href: Route.CERTIFICATION_ISSUERS,
                label: menuLabel(t("admin.certificationIssuers.list.title")),
                icon: <ShieldCheck stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    // Trainer Space submenu
    if (checkFeatureFlag(Feature.MENU_TRAINER_SPACE)) {
        items.push({
            key: "trainer-space",
            label: menuLabel(t("component.Navigation.Menu.items.trainerSpace.label")),
            "data-cy": "menu-submenu-trainer-space",
            icon: <Brief stroke="transparent-base" className="rounded-none" />,
            children: [
                getLink({
                    "data-cy": "menu-item-professional-situations",
                    href: Route.TRAINER_WORKSPACE_PROFESSIONAL_SITUATION,
                    label: menuLabel(t("component.Navigation.Menu.items.trainerSpace.children.professionalSituations")),
                }),
                getLink({
                    "data-cy": "menu-item-all-briefs",
                    href: Route.TRAINER_WORKSPACE_BRIEFS,
                    label: menuLabel(t("component.Navigation.Menu.items.trainerSpace.children.allBriefs")),
                }),
                getLink({
                    "data-cy": "menu-item-frameworks",
                    href: Route.TRAINER_WORKSPACE_FRAMEWORKS,
                    label: menuLabel(t("component.Navigation.Menu.items.frameworks")),
                }),
            ],
        } as MenuItem);
    }

    // Classroom Space submenu
    if (checkFeatureFlag(Feature.MENU_CLASSROOM_SPACE)) {
        const classroomSpaceItems: MenuItem[] = [
            getLink({
                "data-cy": "menu-item-classroom-resources",
                href: interpolate(Route.CLASSROOM_RESOURCES, {
                    classroomId: activeClassroom ? getResourceUuid(activeClassroom) : undefined,
                }),
                label: menuLabel(t("component.Navigation.Menu.items.classroomSpace.children.resources")),
            }),
            getLink({
                "data-cy": "menu-item-classroom-works",
                href: interpolate(Route.CLASSROOM_WORKSPACES, {
                    classroomId: activeClassroom ? getResourceUuid(activeClassroom) : undefined,
                }),
                label: menuLabel(t("component.Navigation.Menu.items.works")),
            }),
        ];

        // Briefs (conditional insertion at index 1)
        if (checkFeatureFlag(Feature.CLASSROOM_BRIEFS) && activeClassroom) {
            classroomSpaceItems.splice(
                1,
                0,
                getLink({
                    "data-cy": "menu-item-classroom-briefs",
                    href: interpolate(Route.CLASSROOM_BRIEFS, {
                        classroomId: getResourceUuid(activeClassroom),
                    }),
                    label: menuLabel(t("component.Navigation.Menu.items.classroomSpace.children.briefs")),
                }),
            );
        } else if (checkFeatureFlag([Feature.TOPICS_WORK_SUBMIT, Feature.WORKSPACE_GROUP_SUBMIT])) {
            classroomSpaceItems.splice(
                1,
                0,
                getLink({
                    "data-cy": "menu-item-classroom-briefs",
                    href: Route.TRAINER_WORKSPACE_BRIEFS,
                    label: menuLabel(t("component.Navigation.Menu.items.classroomSpace.children.briefs")),
                }),
            );
        }

        // Dashboard (conditional insertion at index 0)
        if (checkFeatureFlag(Feature.MENU_CLASSROOM_DASHBOARD) && activeClassroom) {
            classroomSpaceItems.splice(
                0,
                0,
                getLink({
                    "data-cy": "menu-item-classroom-dashboard",
                    href: interpolate(Route.CLASSROOM_DASHBOARD, {
                        classroomId: getResourceUuid(activeClassroom),
                    }),
                    label: menuLabel(t("component.Navigation.Menu.items.classroomSpace.children.dashboard")),
                }),
            );
        }

        // Statistics
        if (checkFeatureFlag(Feature.CLASSROOM_STATISTICS) && activeClassroom) {
            classroomSpaceItems.push(
                getLink({
                    "data-cy": "menu-item-classroom-statistics",
                    href: interpolate(Route.CLASSROOMS_STATISTICS, {
                        classroomId: getResourceUuid(activeClassroom),
                    }),
                    label: menuLabel(t("component.Navigation.Menu.items.classroomSpace.children.statistics")),
                }),
            );
        }

        // Progression
        if (checkFeatureFlag(Feature.CLASSROOM_LEARNER_PROGRESSION) && activeClassroom) {
            classroomSpaceItems.push(
                getLink({
                    "data-cy": "menu-item-classroom-progression",
                    href: interpolate(Route.CLASSROOM_LEARNER_PROGRESSION, {
                        classroomId: getResourceUuid(activeClassroom),
                    }),
                    label: menuLabel(t("component.Navigation.Menu.items.classroomSpace.children.progression")),
                }),
            );
        }

        // Agenda (at index 1, after dashboard if present)
        if (checkFeatureFlag(Feature.AGENDA) && activeClassroom) {
            classroomSpaceItems.splice(
                1,
                0,
                getLink({
                    "data-cy": "menu-item-agenda",
                    href: interpolate(Route.CLASSROOM_AGENDA, { classroomId: getResourceUuid(activeClassroom) }),
                    label: t("component.Navigation.Menu.items.agenda"),
                }),
            );
        }

        items.push({
            key: "classroom-space",
            label: menuLabel(t("component.Navigation.Menu.items.classroomSpace.label")),
            "data-cy": "menu-submenu-classroom-space",
            icon: <Binder stroke="transparent-base" className="rounded-none" />,
            children: [
                {
                    key: "active-classroom",
                    type: "group",
                    label: menuLabel(
                        activeClassroom
                            ? activeClassroom.title
                            : t("component.Navigation.Menu.items.classroomSpace.children.noActiveClassroom"),
                    ),
                    className: "!text-color-ant-secondary text-ant-xs",
                    children: classroomSpaceItems,
                },
            ],
        } as MenuItem);
    }

    // Tutor-specific items (flat, no submenu)
    if (checkFeatureFlag(Feature.FRAMEWORKS_ACCESS_TUTOR)) {
        items.push(
            getLink({
                "data-cy": "menu-item-tutor-frameworks",
                href: Route.FRAMEWORKS,
                label: menuLabel(t("component.Navigation.Menu.items.frameworks")),
                icon: <Brief stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    if (checkFeatureFlag(Feature.WORKSPACES_ACCESS_TUTOR)) {
        items.push(
            getLink({
                "data-cy": "menu-item-tutor-works",
                href: Route.WORKSPACES,
                label: menuLabel(t("component.Navigation.Menu.items.works")),
                icon: <EmptyWork stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    // Follow-ups
    if (checkFeatureFlag(Feature.MENU_FOLLOW_UPS)) {
        items.push(
            getLink({
                "data-cy": "menu-item-follow-ups",
                href: Route.FOLLOW_UPS_LIST,
                label: menuLabel(t("component.Navigation.Menu.items.followUp")),
                icon: <Clipboard stroke="transparent-base" className="rounded-none" />,
            }),
        );
    }

    // Learners
    if (checkFeatureFlag(Feature.LEARNERS_CARD_ACCESS_TUTOR)) {
        items.push(
            getLink({
                "data-cy": "menu-item-learners",
                href: Route.LEARNERS,
                label: menuLabel(t("component.Navigation.Menu.items.learners")),
                icon: <Users size={20} fill="transparent-base" className="rounded-none" />,
            }),
        );
    }

    return items;
};
